import { useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import Modal from '../ui/Modal';
import { EmptyState } from '../ui/EmptyState';
import { GoogleIcon } from '../icons/navIcons';
import { RefreshIcon, TrashIcon, AlertTriangleIcon } from '../icons/uiIcons';
import {
  formatRelativeTime,
  scopeToServices,
  type GoogleAccountDoc,
} from '../../lib/google';

interface ConnectedAccountsCardProps {
  accounts: GoogleAccountDoc[];
  loading: boolean;
  onConnectGoogle: () => void;
  onSyncAccount: (accountId: string) => Promise<void>;
  onUnlinkAccount: (accountId: string) => Promise<void>;
}

export function ConnectedAccountsCard({
  accounts,
  loading,
  onConnectGoogle,
  onSyncAccount,
  onUnlinkAccount,
}: ConnectedAccountsCardProps) {
  const { t } = useI18n();
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [unlinkingAccount, setUnlinkingAccount] = useState<GoogleAccountDoc | null>(null);
  const [busyUnlink, setBusyUnlink] = useState(false);

  const handleSync = async (accountId: string) => {
    try {
      setSyncingId(accountId);
      await onSyncAccount(accountId);
    } finally {
      setSyncingId(null);
    }
  };

  const confirmUnlink = async () => {
    if (!unlinkingAccount) return;
    try {
      setBusyUnlink(true);
      await onUnlinkAccount(unlinkingAccount.id);
      setUnlinkingAccount(null);
    } finally {
      setBusyUnlink(false);
    }
  };

  return (
    <Card className="p-4 sm:p-6 border border-line bg-white shadow-card dark:border-slate-800 dark:bg-slate-900">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-slate-100 dark:border-slate-800">
        <div>
          <h3 className="text-sm font-bold text-ink dark:text-slate-100">
            {t('google.accounts.title') || 'Connected Google Accounts'}
          </h3>
          <p className="text-xs text-ink-3 dark:text-slate-400 mt-0.5">
            {t('google.accounts.subtitle') ||
              'Manage authenticated accounts providing Sheets, Contacts, and Calendar access.'}
          </p>
        </div>

        {accounts.length > 0 && (
          <Button
            variant="secondary"
            size="sm"
            onClick={onConnectGoogle}
            leadingIcon={<GoogleIcon size={16} />}
            className="shrink-0 self-start sm:self-auto"
          >
            {t('google.accounts.connectAnother') || 'Add Google Account'}
          </Button>
        )}
      </div>

      {loading && accounts.length === 0 ? (
        <div className="py-12 flex flex-col items-center justify-center text-ink-3 dark:text-slate-400 gap-2">
          <div className="w-6 h-6 border-2 border-brand border-t-transparent rounded-full animate-spin" />
          <span className="text-xs">{t('common.loading') || 'Loading accounts...'}</span>
        </div>
      ) : accounts.length === 0 ? (
        <EmptyState
          icon={<GoogleIcon size={32} />}
          title={t('google.empty.title') || 'No Google Account Connected'}
          description={
            t('google.empty.desc') ||
            'Connect your Google Workspace or personal Gmail account to unlock Google Sheets sync, People API contacts import, and automated Google Meet calendar scheduling.'
          }
          action={
            <Button
              variant="primary"
              size="md"
              onClick={onConnectGoogle}
              leadingIcon={<GoogleIcon size={18} />}
            >
              {t('google.empty.connectBtn') || 'Sign in with Google'}
            </Button>
          }
          className="py-10"
        />
      ) : (
        <div className="mt-4 space-y-3">
          {accounts.map((account) => {
            const services = scopeToServices(account.scopes);
            const isSyncing = syncingId === account.id;

            return (
              <div
                key={account.id}
                className="flex flex-col md:flex-row md:items-center justify-between p-4 rounded-xl border border-slate-200 bg-slate-50/50 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-800/40 dark:hover:bg-slate-800/70 transition-colors gap-4"
              >
                {/* Account Details */}
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 shadow-sm flex items-center justify-center shrink-0 mt-0.5">
                    <GoogleIcon size={20} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-bold text-ink dark:text-slate-100 truncate">
                        {account.email}
                      </span>
                      {account.status === 'ok' ? (
                        <Badge variant="success" size="sm" dot>
                          {t('google.status.active') || 'Active'}
                        </Badge>
                      ) : (
                        <Badge variant="warning" size="sm" dot>
                          {t('google.status.reauthRequired') || 'Re-auth Required'}
                        </Badge>
                      )}
                    </div>

                    {/* Granted services tags */}
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      {services.sheets && (
                        <span className="text-[10px] px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 font-medium">
                          {t('google.services.sheetsTitle') || 'Sheets'}
                        </span>
                      )}
                      {services.drive && (
                        <span className="text-[10px] px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 font-medium">
                          {t('google.services.driveTitle') || 'Drive'}
                        </span>
                      )}
                      {services.calendar && (
                        <span className="text-[10px] px-2 py-0.5 rounded-md bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300 font-medium">
                          {t('google.services.calendarTitle') || 'Calendar'}
                        </span>
                      )}
                      {services.contacts && (
                        <span className="text-[10px] px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300 font-medium">
                          {t('google.services.contactsTitle') || 'Contacts'}
                        </span>
                      )}
                    </div>

                    <div className="text-[11px] text-ink-4 dark:text-slate-500 mt-1.5">
                      <span>{t('google.accounts.lastSynced') || 'Last synced'}: </span>
                      <span className="font-medium text-ink-3 dark:text-slate-400">
                        {formatRelativeTime(account.lastSyncAt)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 self-end md:self-center shrink-0">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleSync(account.id)}
                    disabled={isSyncing}
                    leadingIcon={
                      <RefreshIcon
                        size={14}
                        className={isSyncing ? 'animate-spin text-brand' : ''}
                      />
                    }
                  >
                    {isSyncing
                      ? t('common.syncing') || 'Syncing...'
                      : t('google.accounts.syncBtn') || 'Sync Data'}
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setUnlinkingAccount(account)}
                    className="text-danger hover:bg-pink-50 hover:text-danger dark:hover:bg-rose-950/30"
                    title={t('google.accounts.unlinkBtn') || 'Unlink Account'}
                  >
                    <TrashIcon size={15} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Unlink Confirmation Modal */}
      {unlinkingAccount && (
        <Modal
          open={Boolean(unlinkingAccount)}
          onClose={() => !busyUnlink && setUnlinkingAccount(null)}
          title={t('google.unlink.title') || 'Unlink Google Account'}
          footer={
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setUnlinkingAccount(null)}
                disabled={busyUnlink}
              >
                {t('common.cancel') || 'Cancel'}
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={confirmUnlink}
                loading={busyUnlink}
              >
                {t('google.unlink.confirmBtn') || 'Unlink Account'}
              </Button>
            </div>
          }
        >
          <div className="space-y-3 text-xs text-ink-2 dark:text-slate-300">
            <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/40 rounded-lg text-amber-800 dark:text-amber-300">
              <AlertTriangleIcon size={18} className="shrink-0 mt-0.5 text-amber-600" />
              <div>
                <p className="font-bold">
                  {t('google.unlink.warningTitle') || 'Are you sure you want to unlink this account?'}
                </p>
                <p className="mt-1 text-[11px] leading-relaxed">
                  {t('google.unlink.warningDesc') ||
                    'Unlinking will revoke stored OAuth tokens. Any automated subscriber sheet appends, contacts syncs, or Google Meet appointment creation using this account will stop immediately.'}
                </p>
              </div>
            </div>

            <p className="text-ink-3 dark:text-slate-400">
              {t('google.unlink.targetAccount') || 'Account to unlink:'}{' '}
              <strong className="text-ink dark:text-slate-200">{unlinkingAccount.email}</strong>
            </p>
          </div>
        </Modal>
      )}
    </Card>
  );
}
